import { useEffect, useState, useRef } from 'react';
import type { User, ForbiddenResponse } from '../lib/types';
import { getAccessToken, getActiveAccount, signIn } from '../lib/auth';

export type MeState =
  | { status: 'loading' }
  | { status: 'signing-in' }
  | { status: 'ok'; user: User }
  | { status: 'forbidden'; reason: string; principal?: ForbiddenResponse['principal'] }
  | { status: 'error'; message: string };

/** Guards the automatic interactive-sign-in retry against redirect loops. */
const RETRY_FLAG = 'wgp-auth-retried';

export function useMe(): MeState {
  const [state, setState] = useState<MeState>({ status: 'loading' });
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const account = getActiveAccount();
    if (!account) {
      setState({ status: 'signing-in' });
      void signIn();
      return;
    }

    let cancelled = false;

    // Silent token renewal can fail transiently (e.g. MSAL "timed_out" when
    // the hidden-iframe refresh is slow) or need interaction. Rather than
    // dead-ending on the error page, retry once with a full interactive
    // redirect; only surface the error if that also fails.
    const retryInteractive = (err: unknown) => {
      if (!sessionStorage.getItem(RETRY_FLAG)) {
        sessionStorage.setItem(RETRY_FLAG, '1');
        setState({ status: 'signing-in' });
        void signIn();
        return;
      }
      setState({ status: 'error', message: err instanceof Error ? err.message : String(err) });
    };

    (async () => {
      let token: string | null = null;
      try {
        token = await getAccessToken();
      } catch (err) {
        if (!cancelled) retryInteractive(err);
        return;
      }
      if (!token) {
        if (!cancelled) retryInteractive(new Error('No token'));
        return;
      }

      try {
        const res = await fetch('/api/me', {
          headers: { 'X-User-Token': token },
        });
        if (res.status === 403) {
          const body = (await res.json()) as ForbiddenResponse;
          if (!cancelled) {
            setState({
              status: 'forbidden',
              reason: body.reason ?? 'Sorry, this view is for Team Contract only.',
              principal: body.principal,
            });
          }
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const user = (await res.json()) as User;
        if (!cancelled) {
          sessionStorage.removeItem(RETRY_FLAG);
          setState({ status: 'ok', user });
        }
      } catch (err) {
        if (!cancelled) {
          setState({ status: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return state;
}